import { indexBy, isNil, reject } from "ramda";
import { maybe } from "shared/maybe";
import { typedJson } from "shared/misc";
import { sanitizeField, sanitizeFieldToPrint } from "./sanitize.js";
export const CUSTOM_FIELD_SUBJECTS = {
    ASSET: "Asset",
    CONTROL_POINT: "Control Point",
    LOCATION: "Location",
    PROCEDURE: "Procedure"
};
export const CUSTOM_FIELD_TYPES = {
    BOOLEAN: "Boolean",
    CHOICES: "Choices",
    FILE: "File",
    FILES: "File (Multiple)",
    PHOTO: "Photo",
    PHOTOS: "Photo (Multiple)",
    NUMBER: "Numeric",
    TEXT: "Text",
    TEXTAREA: "TextArea"
};
export class CustomFieldEntries {
    entries;
    parseId;
    constructor(entries, parseId) {
        this.entries = entries;
        this.parseId = parseId;
    }
    toObject() {
        return Object.fromEntries(this.entries.map(_ => [_.id, sanitizeField(_.definition.type, _.value, this.parseId)]));
    }
    toPrint() {
        return Object.fromEntries(this.entries.map(_ => [_.definition.name, sanitizeFieldToPrint(_.definition.type, _.value)]));
    }
}
export class CustomFieldProcessor {
    fields;
    parseId;
    constructor(fields, parseId) {
        this.fields = fields;
        this.parseId = parseId;
    }
    samples() {
        return new CustomFieldEntries(Object.entries(this.fields).map(([id, definition]) => ({ id, definition, value: cfSample(definition.type) })), this.parseId);
    }
    from(json) {
        const parsed = (() => {
            if (typeof json === "string") {
                return parseCustomFields(json);
            }
            return json;
        })();
        const entries = maybe(parsed, _ => Object.entries(_), () => []);
        const mapped = entries.flatMap(([id, value]) => maybe(this.fields[id], definition => [{ id, definition, value }], () => []));
        return new CustomFieldEntries(mapped, this.parseId);
    }
}
export function cfProcFromHasura(fields, parseId) {
    return new CustomFieldProcessor(indexBy(_ => _.id, fields), parseId);
}
/**
 * Parse custom field values from a JSON string.
 * @param json A JSON string.
 * @returns Custom field values.
 */
export function parseCustomFields(json) {
    const parsed = maybe(json, typedJson);
    if (parsed === undefined || parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
        return {};
    }
    return parsed;
}
/**
 * Turn custom field values into a JSON string.
 * @param values Custom field values.
 * @returns A JSON string.
 */
export function stringifyCustomFields(values) {
    return JSON.stringify(reject(isNil, values));
}
/**
 * Generate a sample value for a given custom field.
 * @param field The custom field.
 * @returns A sample value.
 */
function cfSample(field) {
    switch (field) {
        case "BOOLEAN": return Math.round(Math.random());
        case "CHOICES": return "Choice #" + Math.ceil(Math.random() / .1);
        case "NUMBER": return Math.floor(Math.random() * 1000);
        case "TEXT": return "This is short text.";
        case "FILE": return null;
        case "PHOTO": return null;
        case "FILES": return [];
        case "PHOTOS": return [];
        case "TEXTAREA": return "This is a very long text field that runs on for a lot of extra words that aren't necessary.";
    }
}
